/**
 * OPS interior system — the room behind the OPS building (plot P2).
 *
 * Builds an enclosed room on its own island slot (OPS_ISLAND_Z) that answers,
 * at a glance, three operational questions:
 *   1. what is QUEUED — a lane of abstract work tickets, one per waiting entry, in
 *      position order, head of line at a dispatch desk ("+N more" floor sign
 *      for the overflow);
 *   2. what the DAEMON is doing NOW — a pillar + cap + health-orb obelisk on a
 *      dais (the server-room recipe), pulsing by health status, above a POOL
 *      BOARD of poolSize pegs with activeAgents lit;
 *   3. what waits for a HUMAN — one approval beacon per pending interaction; [E] on the
 *      nearest opens the existing InteractionDialog to answer.
 *
 * Everything authored here is parented to the opsRoot entity passed to init —
 * never the global worldRoot (which stays pointed at the office for the whole
 * session) — so the interiors never write into each other's coordinate space.
 * Queue and approval records use explicitly non-human work objects; room-static
 * colliders register once during init while the caller
 * has the ops island's collider placement set. Remote data comes only from the
 * session-owned projection store.
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import type { DaemonHealth, Interaction, QueueEntry } from '../../api/types'
import { INPUT_PRIORITY, modalInputRouter } from '../../input/modalInputRouter'
import { injectWorldCardStyles } from './labelStyles'
import { computeLabelScreenPos, INTERACT_DIST } from './labels'
import { OPS_ISLAND_Z } from './interiors'
import {
  OPS_HALF_X, OPS_HALF_Z, OPS_WALL_HEIGHT,
  OPS_INCIDENT_ROOM,
  OPS_OBELISK_POS, OPS_DAIS_RADIUS, OPS_DAIS_COLLIDER, OPS_PILLAR_COLLIDER,
  OPS_POOL_Y, OPS_POOL_Z, OPS_POOL_MIN_X, OPS_POOL_MAX_X, OPS_APPROVAL_YAW,
  computeOpsWalls,
  layoutQueueLane, opsOverflowSignPosition,
  layoutPoolPegs,
  layoutApprovals, pendingInteractions,
  type QueueSlot, type ApprovalSlot,
} from './ops'
import {
  buildStandardInteriorShell,
  createRenderEntity,
  createWorldOverlay,
  escapeWorldHtml,
  type OverlayHandle,
} from './interiorShared'
import { createInteriorMaterial, InteriorDressingKit } from './interiorDressing'
import { daemonStatusView } from './statusViewModels'
import {
  createWorkObject,
  createWorkObjectMaterials,
  type WorkObjectHandle,
  type WorkObjectMaterials,
} from './workObject'

/** Show a figure's DOM detail card within this distance (world units). */
const CARD_LABEL_DIST = 9
/** How often the queue/health/interactions are polled while inside the room. */

const QUEUE_STATUS_HEX: Record<string, string> = {
  pending: '#94a3b8',
  dispatched: '#a78bfa',
  running: '#22d3ee',
}

interface QueueFigure {
  slot: QueueSlot
  handle: WorkObjectHandle
}

interface ApprovalFigure {
  slot: ApprovalSlot
  handle: WorkObjectHandle
}

/** Nearest interactable in [E] range: either a run to inspect or an approval. */
interface NearApproval {
  runId: string
  dist: number
}

export class OpsSystem {
  private app!: pc.Application
  private root!: pc.Entity
  private cameraEntity!: pc.Entity

  private container!: HTMLDivElement
  private overlay: OverlayHandle | null = null
  private labels = new Map<string, HTMLDivElement>()
  private tmpVec = new pc.Vec3()

  // Daemon obelisk
  private orbMat!: pc.StandardMaterial
  private capMat!: pc.StandardMaterial
  private pillarMat!: pc.StandardMaterial
  private elapsed = 0
  private currentHealth: DaemonHealth | null = null
  private healthLabel: HTMLDivElement | null = null

  // Pool board
  private poolHolder!: pc.Entity
  private poolPegs: pc.Entity[] = []
  private litPegMat!: pc.StandardMaterial
  private darkPegMat!: pc.StandardMaterial
  private poolKey = ''

  // Queue lane
  private laneHolder!: pc.Entity
  private queueFigures: QueueFigure[] = []
  private queueSway = new Map<string, number>()
  private queueKey = ''
  private overflowSign: pc.Entity | null = null
  private workObjectMaterials!: WorkObjectMaterials

  // Approvals
  private approvalHolder!: pc.Entity
  private approvalFigures: ApprovalFigure[] = []
  private approvalKey = ''
  private nearestApprovalRunId: string | null = null

  private active = false
  private unsubscribe?: () => void
  private disposeInput?: () => void

  init(app: pc.Application, root: pc.Entity, cameraEntity: pc.Entity): void {
    this.app = app
    this.root = root
    this.cameraEntity = cameraEntity
    this.workObjectMaterials = createWorkObjectMaterials()

    this.buildRoom()
    this.buildDispatchIdentity()
    this.buildObelisk()
    this.buildPoolBoard()

    this.laneHolder = new pc.Entity('ops-lane')
    this.root.addChild(this.laneHolder)
    this.approvalHolder = new pc.Entity('ops-approvals')
    this.root.addChild(this.approvalHolder)

    // DOM overlay for figure detail + [E] prompt (hidden until player enters).
    injectWorldCardStyles()
    this.overlay = createWorldOverlay(app, { display: 'none' })
    this.container = this.overlay.element

    this.unsubscribe = useArenaStore.subscribe((state, prev) => {
      if (
        state.health !== prev.health ||
        state.queue !== prev.queue ||
        state.interactionsByRun !== prev.interactionsByRun
      ) this.syncRemoteState()
    })
    this.syncRemoteState()
    this.disposeInput = modalInputRouter.register(INPUT_PRIORITY.room, this.onKeyDown)
  }

  // ── Static room geometry ────────────────────────────────────────────────────

  private addBox(
    name: string,
    type: 'box' | 'plane' | 'cylinder' | 'cone' | 'sphere',
    pos: [number, number, number],
    scale: [number, number, number],
    mat: pc.Material,
    parent: pc.Entity = this.root,
    collider?: import('../colliders').AABB,
  ): pc.Entity {
    return createRenderEntity({
      name, type, position: pos, scale, material: mat, parent, collider,
    })
  }

  private buildRoom(): void {
    buildStandardInteriorShell({
      root: this.root,
      prefix: 'ops',
      halfX: OPS_HALF_X,
      halfZ: OPS_HALF_Z,
      height: OPS_WALL_HEIGHT,
      walls: computeOpsWalls(),
      floorColor: [0.30, 0.31, 0.33],
      backdropColor: [0.075, 0.085, 0.10],
      keyLightY: 3.2,
      keyLightRange: 44,
      accent: {
        name: 'north-light',
        color: [0.72, 0.82, 1],
        y: 2.4,
        z: -OPS_HALF_Z + 4,
        range: 20,
      },
    })
    const zoneMat = (color: [number, number, number]) => {
      const mat = new pc.StandardMaterial()
      mat.diffuse = new pc.Color(...color)
      mat.emissive = new pc.Color(...color)
      mat.emissiveIntensity = 0.28
      mat.gloss = 0.25
      mat.update()
      return mat
    }
    // Low floor fields establish the three operational territories without
    // adding walls or collision obstacles to the hall.
    this.addBox('ops-zone-dispatch', 'box', [-7, 0.025, -2.6], [4.4, 0.05, 9.7], zoneMat([0.12, 0.17, 0.24]))
    this.addBox('ops-zone-daemon', 'cylinder', [0, 0.035, -5], [4.2, 0.07, 4.2], zoneMat([0.10, 0.23, 0.21]))
    this.addBox(
      'ops-zone-incident',
      'box',
      [OPS_INCIDENT_ROOM.centerX, 0.025, OPS_INCIDENT_ROOM.centerZ],
      [OPS_INCIDENT_ROOM.width, 0.05, OPS_INCIDENT_ROOM.depth],
      zoneMat([0.16, 0.20, 0.25]),
    )
    const dressing = new InteriorDressingKit(this.root)
    const frame = createInteriorMaterial([0.12, 0.15, 0.20], { metalness: 0.55, gloss: 0.6 })
    const glass = createInteriorMaterial([0.30, 0.54, 0.78], { opacity: 0.2, gloss: 0.9 })
    const foliage = createInteriorMaterial([0.12, 0.34, 0.25])
    const glow = createInteriorMaterial([0.95, 0.68, 0.34], { emissive: 1.5 })
    dressing.addGlassRoom(
      'ops-incident-room',
      OPS_INCIDENT_ROOM.centerX,
      OPS_INCIDENT_ROOM.centerZ,
      OPS_INCIDENT_ROOM.width,
      OPS_INCIDENT_ROOM.depth,
      glass,
      frame,
    )
    dressing.addCeilingRaft('ops-dispatch-ceiling', -7, -2.6, 4.4, 9.4, OPS_WALL_HEIGHT, frame, glow)
    dressing.addCeilingRaft(
      'ops-incident-ceiling',
      OPS_INCIDENT_ROOM.centerX,
      OPS_INCIDENT_ROOM.centerZ,
      5.2,
      4.4,
      OPS_WALL_HEIGHT,
      frame,
      glow,
    )
    dressing.addCeilingRaft(
      'ops-waiting-ceiling',
      3.7,
      2.5,
      4.2,
      3.4,
      OPS_WALL_HEIGHT,
      frame,
      glow,
    )
    dressing.addPlanter('ops-entry-planter-west', -2.5, 8.2, frame, foliage)
    dressing.addPlanter('ops-entry-planter-east', 2.5, 8.2, frame, foliage)
  }

  /** Identity/status layer mounted behind the licensed dispatch bank. */
  private buildDispatchIdentity(): void {
    const glowMat = new pc.StandardMaterial()
    glowMat.diffuse = new pc.Color(0.5, 0.42, 0.9)
    glowMat.emissive = new pc.Color(0.65, 0.55, 0.98)
    glowMat.emissiveIntensity = 3
    glowMat.useLighting = false
    glowMat.update()
    this.addBox('ops-dispatch-status-rail', 'box',
      [-10.82, 1.35, -2.6], [0.08, 0.08, 8.4], glowMat)
    this.buildTextPanel('ops-dispatch-sign', 'QUEUE + DISPATCH',
      [-7.3, 2.15, -9.76], [4.4, 0.58], '#38bdf8')
  }

  // ── Daemon obelisk (server-room recipe on a dais) ───────────────────────────

  private buildObelisk(): void {
    const [ox, oz] = OPS_OBELISK_POS
    const color = daemonStatusView(null).rgb

    // Dais
    const daisMat = new pc.StandardMaterial()
    daisMat.diffuse = new pc.Color(0.12, 0.13, 0.16)
    daisMat.metalness = 0.4
    daisMat.gloss = 0.5
    daisMat.update()
    this.addBox(
      'ops-dais',
      'cylinder',
      [ox, 0.15, oz],
      [OPS_DAIS_RADIUS * 2, 0.3, OPS_DAIS_RADIUS * 2],
      daisMat,
      this.root,
      OPS_DAIS_COLLIDER,
    )

    // Pillar
    this.pillarMat = new pc.StandardMaterial()
    this.pillarMat.diffuse = new pc.Color(0.06, 0.09, 0.1)
    this.pillarMat.emissive = new pc.Color(color[0] * 0.05, color[1] * 0.05, color[2] * 0.05)
    this.pillarMat.metalness = 0.8
    this.pillarMat.gloss = 0.7
    this.pillarMat.update()
    this.addBox(
      'ops-daemon-pillar',
      'box',
      [ox, 1.6, oz],
      [0.6, 2.6, 0.6],
      this.pillarMat,
      this.root,
      OPS_PILLAR_COLLIDER,
    )

    // Cap
    this.capMat = new pc.StandardMaterial()
    this.capMat.diffuse = new pc.Color(color[0], color[1], color[2])
    this.capMat.emissive = new pc.Color(color[0] * 0.3, color[1] * 0.3, color[2] * 0.3)
    this.capMat.metalness = 0.9
    this.capMat.gloss = 0.8
    this.capMat.update()
    this.addBox('ops-daemon-cap', 'cone', [ox, 3.15, oz], [0.7, 0.5, 0.7], this.capMat)

    // Health orb
    this.orbMat = new pc.StandardMaterial()
    this.orbMat.diffuse = new pc.Color(color[0], color[1], color[2])
    this.orbMat.emissive = new pc.Color(color[0], color[1], color[2])
    this.orbMat.emissiveIntensity = 0.8
    this.orbMat.metalness = 0.3
    this.orbMat.gloss = 0.9
    this.orbMat.update()
    this.addBox('ops-daemon-orb', 'sphere', [ox, 3.1, oz], [0.5, 0.5, 0.5], this.orbMat)

    this.buildHealthLabel()
  }

  private buildHealthLabel(): void {
    const gd = this.app.graphicsDevice as { canvas?: HTMLCanvasElement }
    const parent = gd.canvas?.parentElement
    if (!parent) return
    this.healthLabel = document.createElement('div')
    Object.assign(this.healthLabel.style, {
      position: 'absolute',
      bottom: '1rem',
      left: '1rem',
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: '#475569',
      pointerEvents: 'none',
      zIndex: '5',
      display: 'none',
    })
    this.healthLabel.textContent = 'DAEMON · OFFLINE'
    // Kept inside the ops DOM container so it hides with the room.
    parent.appendChild(this.healthLabel)
  }

  // ── Pool board ──────────────────────────────────────────────────────────────

  private buildPoolBoard(): void {
    this.poolHolder = new pc.Entity('ops-pool')
    this.root.addChild(this.poolHolder)

    this.litPegMat = new pc.StandardMaterial()
    this.litPegMat.diffuse = new pc.Color(0.13, 0.83, 0.93)
    this.litPegMat.emissive = new pc.Color(0.13, 0.83, 0.93)
    this.litPegMat.emissiveIntensity = 2.2
    this.litPegMat.useLighting = false
    this.litPegMat.update()

    this.darkPegMat = new pc.StandardMaterial()
    this.darkPegMat.diffuse = new pc.Color(0.16, 0.18, 0.22)
    this.darkPegMat.emissive = new pc.Color(0.16, 0.18, 0.22)
    this.darkPegMat.emissiveIntensity = 0.15
    this.darkPegMat.useLighting = false
    this.darkPegMat.update()

    // Backing panel + label above the pegs.
    const panelMat = new pc.StandardMaterial()
    panelMat.diffuse = new pc.Color(0.09, 0.10, 0.13)
    panelMat.gloss = 0.2
    panelMat.update()
    this.addBox('ops-pool-panel', 'box',
      [(OPS_POOL_MIN_X + OPS_POOL_MAX_X) / 2, OPS_POOL_Y, OPS_POOL_Z - 0.08],
      [OPS_POOL_MAX_X - OPS_POOL_MIN_X + 0.8, 1.25, 0.1], panelMat)
    this.buildTextPanel('ops-pool-sign', 'AGENT POOL',
      [(OPS_POOL_MIN_X + OPS_POOL_MAX_X) / 2, OPS_POOL_Y + 0.86, OPS_POOL_Z + 0.03],
      [3.8, 0.4],
      '#22d3ee',
    )

    this.syncPool(useArenaStore.getState().health)
  }

  private syncPool(health: DaemonHealth | null): void {
    const poolSize = health?.poolSize ?? 0
    const activeAgents = health?.activeAgents ?? 0
    const key = `${poolSize}/${activeAgents}`
    if (key === this.poolKey) return
    this.poolKey = key

    for (const peg of this.poolPegs) peg.destroy()
    this.poolPegs = []
    for (const peg of layoutPoolPegs(poolSize, activeAgents)) {
      const e = this.addBox(`ops-peg-${peg.index}`, 'sphere',
        [peg.x, peg.y, peg.z + 0.12], [0.34, 0.34, 0.34],
        peg.lit ? this.litPegMat : this.darkPegMat, this.poolHolder)
      this.poolPegs.push(e)
    }
  }

  // ── Canvas text panel helper (banners/signs) ────────────────────────────────

  private buildTextPanel(
    name: string, text: string, pos: [number, number, number], size: [number, number], accent: string,
    parent: pc.Entity = this.root,
  ): pc.Entity {
    const panel = new pc.Entity(name)
    panel.addComponent('render', { type: 'box' })
    panel.setLocalScale(size[0], size[1], 0.04)
    panel.setLocalPosition(pos[0], pos[1], pos[2])
    const mat = new pc.StandardMaterial()
    mat.diffuse = new pc.Color(0.08, 0.09, 0.13)
    mat.emissive = new pc.Color(0.961, 0.922, 0.875)
    mat.emissiveIntensity = 0.5
    try {
      const canvas = document.createElement('canvas')
      canvas.width = 512
      canvas.height = 96
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.fillStyle = '#15181d'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.fillStyle = accent
        ctx.fillRect(0, 0, 12, canvas.height)
        ctx.fillStyle = '#f2ede4'
        ctx.font = '700 44px Nunito, system-ui, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.letterSpacing = '3px'
        ctx.fillText(text, canvas.width / 2 + 6, canvas.height / 2 + 2)
        const tex = new pc.Texture(this.app.graphicsDevice, { width: canvas.width, height: canvas.height })
        tex.setSource(canvas)
        mat.emissiveMap = tex
        mat.emissive = new pc.Color(1, 1, 1)
        mat.emissiveIntensity = 0.85
      }
    } catch { /* keep the plain emissive panel */ }
    mat.update()
    for (const mi of panel.render?.meshInstances ?? []) mi.material = mat
    parent.addChild(panel)
    return panel
  }

  // ── Work records (deliberately not character rigs) ─────────────────────────

  private syncQueue(queue: QueueEntry[]): void {
    const { slots, overflow } = layoutQueueLane(queue)
    // Key on ordered ids so a poll that changed nothing does not rebuild figures.
    const key = slots.map((s) => s.id).join('|') + `#${overflow}`
    if (key === this.queueKey) return
    this.queueKey = key

    for (const f of this.queueFigures) f.handle.destroy()
    this.queueFigures = []
    this.queueSway.clear()

    for (const slot of slots) {
      const handle = createWorkObject(
        this.laneHolder,
        this.workObjectMaterials,
        `ops-queue-ticket-${slot.id}`,
        'queue',
        [slot.x, 0, slot.z],
        0,
      )
      this.queueFigures.push({ slot, handle })
    }

    this.overflowSign?.destroy()
    this.overflowSign = null
    if (overflow > 0) {
      const [x, y, z] = opsOverflowSignPosition()
      this.overflowSign = this.buildTextPanel('ops-queue-overflow', `+${overflow} MORE`,
        [x, y + 0.02, z], [2.6, 0.5], '#94a3b8', this.laneHolder)
      this.overflowSign.setLocalEulerAngles(90, 0, 0) // lie flat on the floor
    }
  }

  private syncApprovals(byRun: Record<string, Interaction[]>): void {
    const { slots } = layoutApprovals(pendingInteractions(byRun))
    const key = slots.map((s) => s.interactionId).join('|')
    if (key === this.approvalKey) return
    this.approvalKey = key

    for (const f of this.approvalFigures) f.handle.destroy()
    this.approvalFigures = []

    for (const slot of slots) {
      const handle = createWorkObject(
        this.approvalHolder,
        this.workObjectMaterials,
        `ops-approval-beacon-${slot.interactionId}`,
        'approval',
        [slot.x, 0, slot.z],
        OPS_APPROVAL_YAW,
      )
      this.approvalFigures.push({ slot, handle })
    }
  }

  private syncRemoteState(): void {
    const { health, queue, interactionsByRun } = useArenaStore.getState()
    this.currentHealth = health
    this.applyHealth(health)
    this.syncPool(health)
    this.syncQueue(queue)
    this.syncApprovals(interactionsByRun)
  }

  private applyHealth(health: DaemonHealth | null): void {
    const view = daemonStatusView(health)
    const color = view.rgb
    this.orbMat.diffuse = new pc.Color(color[0], color[1], color[2])
    this.orbMat.emissive = new pc.Color(color[0], color[1], color[2])
    this.orbMat.update()
    this.capMat.diffuse = new pc.Color(color[0], color[1], color[2])
    this.capMat.emissive = new pc.Color(color[0] * 0.3, color[1] * 0.3, color[2] * 0.3)
    this.capMat.update()
    this.pillarMat.emissive = new pc.Color(color[0] * 0.05, color[1] * 0.05, color[2] * 0.05)
    this.pillarMat.update()

    if (this.healthLabel) {
      this.healthLabel.style.color = view.cssColor
      this.healthLabel.textContent = view.label
    }
  }

  /** Called from the main loop with whether the player is inside the ops room. */
  frame(active: boolean): void {
    if (active !== this.active) {
      this.active = active
      this.container.style.display = active ? 'block' : 'none'
      if (this.healthLabel) this.healthLabel.style.display = active ? 'block' : 'none'
      if (!active) {
        this.nearestApprovalRunId = null
        for (const div of this.labels.values()) div.style.display = 'none'
      }
    }
    if (!active) return

    // Pulse the daemon orb by health status.
    this.elapsed += 1 / 60
    const speed = daemonStatusView(this.currentHealth).pulseSpeed
    this.orbMat.emissiveIntensity = 0.6 + 0.4 * Math.abs(Math.sin(this.elapsed * speed))
    this.orbMat.update()

    // Gentle mechanical scan for the queue tickets; never human idle motion.
    for (const { slot, handle } of this.queueFigures) {
      const t = (this.queueSway.get(slot.id) ?? 0) + 1 / 60
      this.queueSway.set(slot.id, t)
      handle.signal.setLocalEulerAngles(0, t * 45 + slot.slot * 30, 0)
    }

    const camComp = this.cameraEntity.camera
    if (!camComp) return
    const camPos = this.cameraEntity.getPosition()

    interface Framed { key: string; sx: number; sy: number; html: string }
    const framed: Framed[] = []
    let nearest: NearApproval | null = null

    // Queue work-object cards (title + status, no interaction).
    for (const { slot } of this.queueFigures) {
      const wx = slot.x
      const wz = slot.z + OPS_ISLAND_Z
      const dx = camPos.x - wx
      const dz = camPos.z - wz
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist >= CARD_LABEL_DIST) continue
      const screen = computeLabelScreenPos(wx, 1.65, wz, (wv, out) => camComp.worldToScreen(wv, out), this.tmpVec)
      if (!screen) continue
      framed.push({ key: `q:${slot.id}`, sx: screen.x, sy: screen.y, html: this.queueHtml(slot) })
    }

    // Approval beacons: card + [E] Answer; nearest in range wins the prompt.
    for (const { slot } of this.approvalFigures) {
      const wx = slot.x
      const wz = slot.z + OPS_ISLAND_Z
      const dx = camPos.x - wx
      const dz = camPos.z - wz
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist >= CARD_LABEL_DIST) continue
      if (dist < INTERACT_DIST && (!nearest || dist < nearest.dist)) {
        nearest = { runId: slot.runId, dist }
      }
      const screen = computeLabelScreenPos(wx, 1.65, wz, (wv, out) => camComp.worldToScreen(wv, out), this.tmpVec)
      if (!screen) continue
      framed.push({ key: `a:${slot.interactionId}`, sx: screen.x, sy: screen.y, html: '' })
    }
    this.nearestApprovalRunId = nearest?.runId ?? null

    // Fill approval html now that we know which is nearest.
    for (const f of framed) {
      if (f.html === '' && f.key.startsWith('a:')) {
        const interactionId = f.key.slice(2)
        f.html = this.approvalHtml(interactionId)
      }
    }

    const visible = new Set<string>()
    for (const f of framed) {
      visible.add(f.key)
      const div = this.getOrCreateLabel(f.key)
      div.style.display = 'block'
      div.style.left = `${f.sx}px`
      div.style.top = `${f.sy}px`
      div.style.transform = 'translate(-50%, -100%)'
      div.innerHTML = f.html
    }
    for (const [key, div] of this.labels) {
      if (!visible.has(key)) div.style.display = 'none'
    }
  }

  private queueHtml(slot: QueueSlot): string {
    const hex = QUEUE_STATUS_HEX[slot.status] ?? '#94a3b8'
    let html = '<div class="wcard">'
    html += `<div class="wcard-title">#${slot.slot + 1} · ${escapeWorldHtml(slot.title || slot.taskId)}</div>`
    html += '<div class="wcard-meta">'
    html += `<span class="wcard-pill" style="background:${hex}">${escapeWorldHtml(slot.status.toUpperCase())}</span>`
    html += `<span>${escapeWorldHtml(slot.taskId)}</span>`
    html += '</div></div>'
    return html
  }

  private approvalHtml(interactionId: string): string {
    const interaction = this.findInteraction(interactionId)
    if (!interaction) return ''
    const isNearest = interaction.runId === this.nearestApprovalRunId
    let html = '<div class="wcard">'
    html += `<div class="wcard-title">NEEDS YOU · ${escapeWorldHtml(interaction.type.toUpperCase())}</div>`
    html += `<div class="wcard-meta"><span>${escapeWorldHtml(interaction.prompt.slice(0, 80))}</span></div>`
    if (interaction.toolName) {
      html += `<div class="wcard-meta"><span>tool: ${escapeWorldHtml(interaction.toolName)}</span></div>`
    }
    if (isNearest) {
      html += '<div class="wcard-kbd-row"><span class="wcard-kbd">E</span> Answer</div>'
    }
    html += '</div>'
    return html
  }

  private findInteraction(interactionId: string): Interaction | null {
    const byRun = useArenaStore.getState().interactionsByRun
    for (const list of Object.values(byRun)) {
      for (const i of list) if (i.id === interactionId) return i
    }
    return null
  }

  private getOrCreateLabel(key: string): HTMLDivElement {
    let div = this.labels.get(key)
    if (!div) {
      div = document.createElement('div')
      Object.assign(div.style, {
        position: 'absolute',
        fontFamily: 'ui-monospace, monospace',
        fontSize: '12px',
        userSelect: 'none',
      })
      this.container.appendChild(div)
      this.labels.set(key, div)
    }
    return div
  }

  // ── [E]: open the existing InteractionDialog on the nearest approval ────────

  private readonly onKeyDown = (e: KeyboardEvent): boolean => {
    if (e.code !== 'KeyE') return false
    if (!this.active) return false
    const state = useArenaStore.getState()
    if (!state.isPlaying) return false
    if (state.activeInteraction !== null) return false
    const runId = this.nearestApprovalRunId
    if (!runId) return false
    const interactions = state.interactionsByRun[runId] ?? []
    const pending = interactions.find((i) => !i.respondedAt)
    if (!pending) return false
    // Reuse the existing dialog + respondToInteraction path entirely.
    state.setActiveInteraction(pending)
    document.exitPointerLock()
    return true
  }

  destroy(): void {
    this.disposeInput?.()
    this.disposeInput = undefined
    this.unsubscribe?.()
    this.unsubscribe = undefined
    for (const f of this.queueFigures) f.handle.destroy()
    this.queueFigures = []
    for (const f of this.approvalFigures) f.handle.destroy()
    this.approvalFigures = []
    this.overflowSign?.destroy()
    this.overflowSign = null
    this.healthLabel?.remove()
    this.overlay?.dispose()
    this.overlay = null
    this.labels.clear()
  }
}
